import { CollapsibleBox, CollapsibleBoxContainer } from '@/components/shared/CollapsibleBox';
import { adjustContentVisibility, Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { Box, Flex } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import styled from 'styled-components';
import { type FaqInfoProps } from './FaqData';

export const SectionWrapperInner = styled(Flex).attrs({
  justifyContent: 'center',
  flexDirection: 'column',
})`
  ${MEDIA_QUERIES.tabletAndDesktop`
        max-width: 1144px;
        width: 100%;
    `}
`;

const CTA = styled(JoinCTA)`
  padding-inline: 29px;
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility}
`;

export interface FaqProps extends React.ComponentProps<'div'> {
  data?: FaqInfoProps[];
  darkBackground?: boolean;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  ctaTitle?: string;
  faqInfo: (isMobile: boolean) => FaqInfoProps[];

  showDisclaimer?: boolean;
  layout?: {
    pt?: number[] | number;
    mt?: number[] | number;
  };
}

export const Faq: React.FC<FaqProps> = ({
  title = 'Good to know...',
  subtitle = 'Homeowners insurance can be confusing, so we worked hard to make it simple.\nIf you still have questions, you can check out <a aria-labelledby="good-to-know-subtitle" href="https://www.lemonade.com/homeowners/explained/" target="_blank" rel="noreferrer">homeowners insurance explained</a>.',
  ctaTitle = "Didn't find what you were looking for?",
  ctaUrl = '/faq#homeowners-insurance',
  ctaLabel = 'Visit our FAQ',
  faqInfo,
  layout,
  showDisclaimer = true,
  ...props
}) => {
  const { trackEvent } = useAnalyticsContext();

  const petFaqTrackEvent = ({ eventName, analytics }: { eventName: string; analytics: string }) =>
    trackEvent(eventName, {
      product: 'pet',
      analytics,
    });

  return (
    <StyledSection pt={[30, 60]} pb={[60, 100]} px={20} {...props} {...layout}>
      <SectionWrapperInner justifyContent="center" alignItems={['normal', 'center']}>
        <Flex mb="60px" flexDirection="column" gap="15px">
          <SectionTitle tabIndex={0}>{parse(title)}</SectionTitle>
          <SectionParagraph id="good-to-know-subtitle">{parse(subtitle)}</SectionParagraph>
        </Flex>
        <CollapsibleBoxContainer minBoxesHeight="325px">
          {faqInfo(false).map(({ question, answer, analytics }) => (
            <CollapsibleBox
              key={question}
              title={question}
              content={answer}
              onMouseEnter={() =>
                petFaqTrackEvent({
                  eventName: 'faq.hovered',
                  analytics,
                })
              }
              onClick={() =>
                petFaqTrackEvent({
                  eventName: 'faq.clicked',
                  analytics,
                })
              }
            />
          ))}
        </CollapsibleBoxContainer>

        {showDisclaimer && (
          <Flex mt="40px" gap="40px" flexDirection="column" justifyContent="center" alignItems="center">
            <SectionParagraph>{ctaTitle}</SectionParagraph>
            <Box>
              <CTA size="small" flavor="standard" customUrl={ctaUrl}>
                {ctaLabel}
              </CTA>
            </Box>
          </Flex>
        )}
      </SectionWrapperInner>
    </StyledSection>
  );
};
